import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"; // ✅ your DialogBoss (Radix/shadcn wrapper)
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronDown,
  ChevronUp,
  Edit,
  Loader,
  PlusCircle,
  Save,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import {
  createNewEmailTemplate,
  deleteCustomEmailTemplate,
  fetchAllEmailTemplates,
  saveCustomEmailTemplate,
  updateCustomEmailTemplate,
} from "../Redux/ReduxSlices/EmailTemplateSlice";
import { useAppContext } from "../context/AppContext";
import EmailTemplatePopup from "./EmailTemplatePopup";

const EmailTemplateManager = () => {
  const dispatch = useDispatch();
  const { templates = [], loading } = useSelector((state) => state.customEmail);
  const { promptInput } = useAppContext();

  // Track which template is expanded
  const [expandedId, setExpandedId] = useState(null);

  // Edit state
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({});

  // Delete
  const [deleteId, setDeleteId] = useState(null);

  // Modals
  const [showEditImpactModal, setShowEditImpactModal] = useState(false);
  const [pendingEditTemplate, setPendingEditTemplate] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [newTemplateData, setNewTemplateData] = useState({
    subject_data: "",
    body_data: "",
    footer_data: "",
  });

  // AI
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiWebsiteUrl, setAiWebsiteUrl] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(fetchAllEmailTemplates());
  }, [dispatch]);

  // Helper: strip HTML tags for plain-text display
  // ✅ Remove ALL HTML tags and return clean text
  const stripHtml = (html) => {
    try {
      const doc = new DOMParser().parseFromString(html || "", "text/html");
      return (doc.body.textContent || "").replace(/\u00A0/g, " ").trim();
    } catch {
      return (html || "").replace(/<[^>]*>/g, "").trim();
    }
  };

  const canCreate = useMemo(() => {
    return (
      newTemplateData.subject_data.trim() &&
      newTemplateData.body_data.trim() &&
      newTemplateData.footer_data.trim()
    );
  }, [newTemplateData]);

  const hasGenerated = useMemo(() => {
    return Boolean(
      (newTemplateData.subject_data && newTemplateData.subject_data.trim()) ||
      (newTemplateData.body_data && newTemplateData.body_data.trim()) ||
      (newTemplateData.footer_data && newTemplateData.footer_data.trim()),
    );
  }, [newTemplateData]);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
    setEditId(null);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNewTemplateChange = (field, value) => {
    setNewTemplateData((prev) => ({ ...prev, [field]: value }));
  };

  // Generate email with AI using backend API
  const handleGenerateTemplateWithAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Please enter a prompt for AI template generation");
      return;
    }

    try {
      setAiGenerating(true);

      let finalPrompt = aiPrompt.trim();
      if (aiWebsiteUrl.trim()) {
        finalPrompt = `${aiPrompt.trim()} please review our website: ${aiWebsiteUrl.trim()}. Use the business model, services, and brand voice to create a well-structured, professional, and conversion-driven email that resonates with the target audience and encourages action;`;
      }

      const payload = { prompt: finalPrompt, user_prompt: promptInput };

      const res = await dispatch(createNewEmailTemplate(payload)).unwrap();
      const tpl = res?.data || res || {};

      // ✅ store sanitized plain text (NO TAGS)
      setNewTemplateData({
        subject_data: stripHtml(tpl.subject_data || ""),
        body_data: stripHtml(tpl.body_data || ""),
        footer_data: stripHtml(tpl.footer_data || ""),
      });

      toast.success("AI email template generated successfully");
    } catch (err) {
      toast.error("Failed to generate template with AI ❌");
    } finally {
      setAiGenerating(false);
    }
  };

  // ✅ IMPORTANT: return promise so Dialog confirm button can await
  const handleCreateTemplate = () => {
    if (!newTemplateData.subject_data.trim()) {
      toast.error("Subject is required");
      return Promise.reject("subject-required");
    }

    return dispatch(saveCustomEmailTemplate(newTemplateData))
      .unwrap()
      .then(() => {
        toast.success("Email template created successfully!");
        dispatch(fetchAllEmailTemplates());

        // close modals
        setShowConfirmModal(false);
        setShowCreateModal(false);

        // reset
        setNewTemplateData({
          subject_data: "",
          body_data: "",
          footer_data: "",
        });
        setAiPrompt("");
        setAiWebsiteUrl("");
      })
      .catch((err) => {
        toast.error("Failed to create email template");
        console.error("Create failed:", err);
        throw err;
      });
  };

  const confirmDelete = () => {
    if (!deleteId) return;

    dispatch(deleteCustomEmailTemplate(deleteId))
      .unwrap()
      .then(() => {
        toast.success("Email template deleted successfully!");
        dispatch(fetchAllEmailTemplates());
        setDeleteId(null);
      })
      .catch((err) => {
        toast.error("Failed to delete email template");
        console.error("Delete failed:", err);
        setDeleteId(null);
      });
  };

  const toggleEdit = (id, template) => {
    // If already editing → SAVE directly
    if (editId === id) {
      if (!formData.subject_data?.trim()) {
        toast.error("Subject is required");
        return;
      }

      if (!formData.body_data?.trim()) {
        toast.error("Body content is required");
        return;
      }

      if (!formData.footer_data?.trim()) {
        toast.error("Footer is required");
        return;
      }
      dispatch(updateCustomEmailTemplate({ id, payload: formData }))
        .unwrap()
        .then(() => {
          toast.success("Email template updated successfully!");
          dispatch(fetchAllEmailTemplates());
          setEditId(null);
        })
        .catch(() => {
          toast.error("Failed to update email template");
        });
      return;
    }

    // Otherwise → show impact confirmation modal
    setPendingEditTemplate({ id, template });
    setShowEditImpactModal(true);
  };

  const confirmEditTemplate = () => {
    if (!pendingEditTemplate) return;

    setEditId(pendingEditTemplate.id);
    setFormData({ ...pendingEditTemplate.template });

    setShowEditImpactModal(false);
    setPendingEditTemplate(null);
  };

  return (
    <div className="min-h-screen text-gray-900 flex flex-col">
      <Header />

      <main className="flex-grow container mx-auto px-10 max-md:px-4 max-md:w-full">
        {/* Heading + Create */}
        <div className="flex max-md:flex-col max-md:gap-3 justify-between items-center max-md:items-start p-4 max-md:p-2 max-md:pt-4 mb-8 max-md:mb-4">
          <div>
            <h4>Your Email Templates</h4>
            <p className="text-gray-600 text-sm sm:text-base">
              Manage and create email templates for your campaigns
            </p>
          </div>
          {templates.length !== 0 && (
            <div className="max-md:w-full flex justify-center ">
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center justify-center max-md:mx-6 font-medium rounded-md
        px-4 py-2 text-sm    main-button text-white shadow-lg max-md:px-2
        hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
              >
                <PlusCircle size={20} className="mr-2" />
                <span>Create New Email Template</span>
              </button>
            </div>
          )}
        </div>

        {/* Templates Grid */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
          {templates.map((template, index) => {
            const templateId = template.id || `temp-${index}`;
            const isExpanded = expandedId === templateId;
            const isEditing = editId === templateId;

            return (
              <div
                key={templateId}
                className={`group bg-white rounded-xl shadow-sm border border-gray-200
                  hover:border-blue-200 hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col
                  ${isExpanded ? "shadow-lg border-blue-100 max-h-full" : "h-48"}`}
              >
                {/* Header */}
                <div
                  onClick={() => toggleExpand(templateId)}
                  className="p-5 max-md:px-4 max-md:py-2 flex flex-col sm:flex-row justify-between items-start cursor-pointer bg-gray-50
                    hover:bg-gray-100 transition-all duration-200 border-b border-gray-100"
                >
                  <div className="flex-1 max-w-full pr-4">
                    <h3
                      className="font-bold text-gray-900 text-lg mb-2 line-clamp-1"
                      title={template.subject_data}
                    >
                      {template.subject_data &&
                        template.subject_data.length > 100
                        ? template.subject_data.substring(0, 100) + "..."
                        : template.subject_data}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed">
                      {stripHtml(template.body_data)}
                    </p>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(templateId);
                    }}
                    className="mt-3 max-md:mt-1 sm:mt-0 p-2 max-md:p-1 rounded-full bg-gray-100 hover:bg-gray-200
                      transition-all duration-200 transform hover:scale-105"
                  >
                    {isExpanded ? (
                      <ChevronUp size={20} className="text-gray-700" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-700" />
                    )}
                  </button>
                </div>

                {/* Expanded content */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="p-5 pt-6 max-md:pt-2 space-y-6 bg-gradient-to-b from-white to-gray-50">
                        <div className="flex justify-end gap-3 border-b pb-4 max-md:pb-2">
                          <button
                            onClick={() => toggleEdit(templateId, template)}
                            className={`flex items-center gap-2 px-4 py-0.5 rounded-md transition-all duration-200 text-sm
                              ${isEditing
                                ? "bg-green-500 hover:bg-green-600 text-white shadow-md"
                                : "bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200"
                              }`}
                            disabled={loading && editId === templateId}
                          >
                            {editId === templateId && loading ? (
                              <Loader size={14} className="animate-spin" />
                            ) : editId === templateId ? (
                              <>
                                <Save size={14} />
                                <span>Save</span>
                              </>
                            ) : (
                              <>
                                <Edit size={14} />
                                <span>Edit</span>
                              </>
                            )}
                          </button>

                          {/* <button
                            onClick={() => setDeleteId(templateId)}
                            className="flex items-center gap-2 px-4 py-0.5 rounded-md border border-red-200
                              bg-red-50 hover:bg-red-100 text-red-600 transition-all duration-200 text-sm"
                          >
                            <Trash2 size={14} />
                            <span>Delete</span>
                          </button> */}
                        </div>

                        {/* Subject */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                            <label className="text-sm font-semibold text-gray-700">
                              Subject
                            </label>
                          </div>
                          <input
                            type="text"
                            value={
                              isEditing
                                ? formData.subject_data || ""
                                : template.subject_data || ""
                            }
                            onChange={(e) =>
                              handleChange("subject_data", e.target.value)
                            }
                            readOnly={!isEditing}
                            className={`w-full rounded-lg px-4 py-3 transition-all duration-200
                              ${isEditing
                                ? "bg-white border-2 border-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                : "bg-gray-50 border border-gray-200 text-gray-700"
                              }`}
                          />
                        </div>

                        {/* Body */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                            <label className="text-sm font-semibold text-gray-700">
                              Body Content
                            </label>
                          </div>
                          <textarea
                            value={stripHtml(
                              isEditing
                                ? formData.body_data || ""
                                : template.body_data || "",
                            )}
                            onChange={(e) =>
                              handleChange("body_data", e.target.value)
                            }
                            readOnly={!isEditing}
                            className={`w-full rounded-lg px-4 py-3 transition-all duration-200 min-h-[250px]
                              ${isEditing
                                ? "bg-white border-2 border-green-300 focus:border-green-500 focus:ring-2 focus:ring-green-200"
                                : "bg-gray-50 border border-gray-200 text-gray-700"
                              }`}
                            rows="4"
                          />
                        </div>

                        {/* Footer */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full" />
                            <label className="text-sm font-semibold text-gray-700">
                              Footer
                            </label>
                          </div>
                          <textarea
                            value={stripHtml(
                              isEditing
                                ? formData.footer_data || ""
                                : template.footer_data || "",
                            )}
                            onChange={(e) =>
                              handleChange("footer_data", e.target.value)
                            }
                            readOnly={!isEditing}
                            className={`w-full rounded-lg px-4 py-3 transition-all duration-200
                              ${isEditing
                                ? "bg-white border-2 border-purple-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                                : "bg-gray-50 border border-gray-200 text-gray-700"
                              }`}
                            rows="2"
                          />
                        </div>
                      </div>

                      <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
                        <div className="flex justify-between items-center text-xs text-gray-500">
                          <span>
                            Last updated: {new Date().toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {!isExpanded && (
                  <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
                    <div className="flex justify-between items-center text-xs text-gray-500">
                      <span>
                        Last updated: {new Date().toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {templates.length === 0 && (
            <div className="col-span-2 flex flex-col items-center justify-center py-16 px-4 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                <svg
                  className="w-10 h-10 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No Email Templates Yet
              </h3>
              <p className="text-gray-500 text-sm mb-6 max-w-md">
                Create your first email template to get started with sending
                emails and launching campaigns.
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-5 py-2 max-md:py-1.5 max-md:px-3 rounded-lg
                  text-white text-sm font-medium main-button  hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
              >
                <PlusCircle size={18} className="mr-2" />
                Create Your First Email Template
              </button>
            </div>
          )}
        </div>
      </main>

      <div className="pt-8">
        <Footer />
      </div>

      {/* ✅ DELETE DIALOG */}
      <Dialog
        open={Boolean(deleteId)}
        onOpenChange={(open) => !open && setDeleteId(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
            <DialogDescription className="text-gray-700">
              Are you sure you want to delete this email template? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-end gap-3 sm:justify-end">
            <button
              onClick={() => setDeleteId(null)}
              className="px-5 py-2.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all duration-200 font-medium"
              disabled={loading}
            >
              Cancel
            </button>

            <button
              onClick={confirmDelete}
              disabled={loading}
              className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-red-600 to-red-700
                text-white hover:from-red-700 hover:to-red-800 transition-all duration-200
                font-medium flex items-center gap-2 min-w-[110px] justify-center"
            >
              {loading ? (
                <Loader size={18} className="animate-spin text-white" />
              ) : (
                <>
                  <Trash2 size={16} />
                  Delete
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ✅ CREATE TEMPLATE DIALOG (big) */}
      {/* <Dialog
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        className="  "
      >
        <DialogContent
          className="
      max-w-3xl
      w-[calc(100vw-2rem)]
       
      p-0
    "
        >
          <div className="flex flex-col max-h-[92vh] ">
            <div className="p-6 border-b ">
              <DialogHeader>
                <DialogTitle>Create New Email Template</DialogTitle>
                <DialogDescription className="text-gray-700">
                  Generate with AI, edit the content, then save your template.
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  Enter your business website (optional)
                </label>
                <input
                  value={aiWebsiteUrl}
                  onChange={(e) => setAiWebsiteUrl(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-4 py-3 text-sm bg-white text-black focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                  placeholder="Enter your Website"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-2">
                  Provide some details about your Business
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-4 py-2 text-sm bg-white text-black 
             focus:border-blue-500 focus:ring-1 focus:ring-blue-500 
             transition-all resize-none"
                  placeholder={
                    "Examples:\n1. We manufacture and distribute food products.\n2. We provide IT talent to IT companies"
                  }
                />
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleGenerateTemplateWithAI}
                  disabled={aiGenerating || !aiPrompt.trim()}
                  className={`px-4 sm:px-5 py-2 rounded-md main-button text-white focus:outline-none focus:ring-2 focus:ring-blue-500
              flex items-center justify-center min-w-[160px] transition-all ${
                aiGenerating || !aiPrompt.trim()
                  ? "opacity-70 cursor-not-allowed"
                  : ""
              }`}
                >
                  {aiGenerating ? (
                    <>
                      <Loader size={18} className="animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    "Generate Email with AI"
                  )}
                </button>
              </div>

              {hasGenerated && (
                <div className="mt-4 border rounded-md p-4 bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">
                    Edit Email Content
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-gray-700">
                        Subject
                      </label>
                      <input
                        value={stripHtml(newTemplateData.subject_data)}
                        onChange={(e) =>
                          handleNewTemplateChange(
                            "subject_data",
                            e.target.value,
                          )
                        }
                        className="mt-1 w-full p-2 border rounded bg-white text-black focus:ring-1 focus:ring-blue-500"
                        placeholder="Enter subject"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-medium text-gray-700">
                        Body
                      </label>
                      <textarea
                        rows={10}
                        value={stripHtml(newTemplateData.body_data)}
                        onChange={(e) =>
                          handleNewTemplateChange("body_data", e.target.value)
                        }
                        className="mt-1 w-full p-2 border rounded bg-white text-black whitespace-pre-wrap focus:ring-1 focus:ring-blue-500"
                        placeholder="Enter email body"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-medium text-gray-700">
                        Footer
                      </label>
                      <textarea
                        rows={3}
                        value={stripHtml(newTemplateData.footer_data)}
                        onChange={(e) =>
                          handleNewTemplateChange("footer_data", e.target.value)
                        }
                        className="mt-1 w-full p-2 border rounded bg-white text-black whitespace-pre-wrap focus:ring-1 focus:ring-blue-500"
                        placeholder="Enter footer"
                      />
                    </div>
                  </div>
                </div>
              )}

              {canCreate && (
                <div className="text-xs text-gray-600">
                  AI generated content ready — click <b>Save Template</b> to
                  continue.
                </div>
              )}
            </div>

            <div className="p-6 border-t flex flex-col-reverse sm:flex-row justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-3 py-2 rounded-md bg-white text-black shadow-lg border hover:bg-gray-200 transition-colors w-full sm:w-auto"
              >
                Cancel
              </button>

              <button
                onClick={() => setShowConfirmModal(true)}
                disabled={loading || !canCreate}
                className={`px-3 py-2 rounded-md shadow-lg flex items-center justify-center min-w-[120px] w-full sm:w-auto transition-all ${
                  canCreate
                    ? "main-button text-white"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                }`}
              >
                {loading ? (
                  <Loader size={18} className="animate-spin text-white" />
                ) : (
                  "Save Template"
                )}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog> */}
      <EmailTemplatePopup
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          // Optional callback after successful create/update
          // console.log("Template saved successfully");
        }}
        existingTemplate={null} // Pass template object here for edit mode
      />

      {/* ✅ CONFIRM CREATE DIALOG */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Create New Template</DialogTitle>
            <DialogDescription className="text-gray-700">
              Are you sure you want to create this new email template?
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-end gap-3 sm:justify-end">
            <button
              onClick={() => setShowConfirmModal(false)}
              className="px-5 py-2.5 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>

            <button
              onClick={handleCreateTemplate} // ✅ will close create+confirm on success
              disabled={loading}
              className="px-5 py-2.5 rounded-md main-button shadow-lg text-white flex items-center justify-center min-w-[90px]"
            >
              {loading ? (
                <Loader size={18} className="animate-spin text-white" />
              ) : (
                "Confirm"
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ✅ EDIT IMPACT DIALOG */}
      <Dialog open={showEditImpactModal} onOpenChange={setShowEditImpactModal}>
        <DialogContent
          className="
      w-[calc(100vw-2rem)]
      max-w-xl
     
      p-4 sm:p-6
      rounded-2xl
    "
        >
          <DialogHeader className="text-center sm:text-left">
            <DialogTitle className="text-base sm:text-lg font-bold">
              Update Campaign Emails?
            </DialogTitle>

            <DialogDescription className="text-gray-700 text-sm sm:text-base leading-relaxed">
              If you edit this email template,{" "}
              <span className="font-semibold text-gray-800">
                all active and future campaigns using this template
              </span>{" "}
              will automatically use the updated version.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-xs sm:text-sm text-yellow-900">
            This change may affect emails that are already scheduled or running.
          </div>

          <DialogFooter className="pt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            {/* Cancel (full width on mobile) */}
            <button
              onClick={() => setShowEditImpactModal(false)}
              className="
          w-full sm:w-auto
          px-4 py-2.5
          rounded-md
          bg-gray-100 text-gray-700
          hover:bg-gray-200
          transition
        "
            >
              Cancel
            </button>

            {/* Confirm (full width on mobile) */}
            <button
              onClick={confirmEditTemplate}
              className="
          w-full sm:w-auto
          px-4 py-2.5
          rounded-md
          main-button
          text-white
          shadow-md
          transition
        "
            >
              Yes, Edit Template
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplateManager;
